import { useEffect, useState } from "react";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import {
  Loader2, IndianRupee, TrendingUp, Download, Receipt, Sparkles, Hotel, Utensils, Coffee
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

const INDUSTRY_ICON = {
  hotel: Hotel,
  restaurant: Utensils,
  cafe: Coffee,
};

export default function Payments() {
  const [data, setData] = useState(null);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    api.get("/payments").then((r) => setData(r.data)).catch(() => setData({ items: [], total_inr: 0, count: 0, by_industry: [] }));
  }, []);

  const exportCsv = async () => {
    setExporting(true);
    try {
      const token = localStorage.getItem("lumiar_token");
      const res = await fetch(`${API}/payments/export.csv`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const cd = res.headers.get("Content-Disposition") || "";
      const m = cd.match(/filename="?([^";]+)"?/i);
      a.download = m ? m[1] : "ipnova_payments.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Exported");
    } catch (e) {
      toast.error(`Export failed: ${e.message}`);
    } finally {
      setExporting(false);
    }
  };

  if (!data) {
    return <div className="p-10"><Loader2 className="w-5 h-5 text-zinc-500 animate-spin"/></div>;
  }

  const avg = data.count ? Math.round(data.total_inr / data.count) : 0;

  return (
    <div className="px-6 lg:px-10 py-8 max-w-7xl mx-auto" data-testid="payments-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs tracking-[0.2em] uppercase text-emerald-400 mb-2">Advance collections</div>
          <h1 className="font-display text-3xl sm:text-4xl tracking-tighter font-medium">Payments</h1>
          <p className="text-zinc-400 mt-2 text-sm max-w-lg">
            Every advance a customer paid on your chat agents. Money settles directly to <span className="text-white">your Razorpay account</span> — this page is your history log.
          </p>
        </div>
        <Button onClick={exportCsv} disabled={exporting || !data.count}
          className="bg-white text-black hover:bg-zinc-200 h-10" data-testid="payments-export-btn">
          {exporting ? <Loader2 className="w-4 h-4 animate-spin mr-1.5"/> : <Download className="w-4 h-4 mr-1.5"/>}
          Export CSV
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-8">
        <Kpi icon={IndianRupee} label="Total collected" value={`₹${data.total_inr.toLocaleString()}`} accent/>
        <Kpi icon={Receipt} label="Payments" value={data.count}/>
        <Kpi icon={TrendingUp} label="Avg advance" value={data.count ? `₹${avg.toLocaleString()}` : "—"}/>
      </div>

      {data.by_industry.length > 0 && (
        <div className="mt-6 bg-zinc-950 border border-white/10 rounded-xl p-5" data-testid="payments-by-industry">
          <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 mb-3">By business type</div>
          <div className="flex flex-wrap gap-2">
            {data.by_industry.map((row) => {
              const Icon = INDUSTRY_ICON[row.industry] || Sparkles;
              return (
                <div key={row.industry} className="bg-black/50 border border-white/10 rounded-lg px-4 py-2 flex items-center gap-3">
                  <Icon className="w-4 h-4 text-emerald-400"/>
                  <div>
                    <div className="text-[10px] tracking-widest uppercase text-zinc-500 capitalize">{row.industry}</div>
                    <div className="font-display text-lg tracking-tight">₹{row.total_inr.toLocaleString()}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="mt-6 bg-zinc-950 border border-white/10 rounded-xl overflow-hidden">
        <div className="p-5 border-b border-white/10">
          <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-500">Recent payments</div>
        </div>
        {data.items.length === 0 ? (
          <div className="p-12 text-center" data-testid="payments-empty">
            <div className="w-14 h-14 rounded-full bg-emerald-500/10 border border-emerald-500/30 grid place-items-center mx-auto mb-3">
              <IndianRupee className="w-6 h-6 text-emerald-400"/>
            </div>
            <div className="font-display text-lg tracking-tight">No advances yet</div>
            <div className="text-xs text-zinc-500 mt-1 max-w-sm mx-auto">
              Enable payments on any of your AI Agents (hotel, restaurant, cafe, etc.) and every customer payment will land here.
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="payments-table">
              <thead>
                <tr className="text-[10px] tracking-widest uppercase text-zinc-500 border-b border-white/5">
                  <th className="text-left px-5 py-3 font-normal">When</th>
                  <th className="text-left px-5 py-3 font-normal">Agent</th>
                  <th className="text-left px-5 py-3 font-normal">Customer</th>
                  <th className="text-right px-5 py-3 font-normal">Amount</th>
                  <th className="text-left px-5 py-3 font-normal">Razorpay ID</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((p) => (
                  <tr key={p.session_id} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`payment-row-${p.session_id}`}>
                    <td className="px-5 py-3 text-xs text-zinc-400 whitespace-nowrap">
                      {p.paid_at ? new Date(p.paid_at).toLocaleString() : "-"}
                    </td>
                    <td className="px-5 py-3">
                      <div className="text-zinc-300 truncate max-w-[200px]">{p.agent_name}</div>
                      <div className="text-[11px] text-zinc-500 capitalize">{p.agent_industry}</div>
                    </td>
                    <td className="px-5 py-3">
                      <div className="text-zinc-300 truncate max-w-[160px]">{p.customer_name || "—"}</div>
                      <div className="text-[11px] text-zinc-500 font-mono">{p.customer_phone || ""}</div>
                    </td>
                    <td className="px-5 py-3 text-right">
                      <div className="font-display text-lg tracking-tighter text-emerald-400">₹{p.amount_inr.toLocaleString()}</div>
                      <Badge className={`text-[10px] mt-0.5 ${p.mode === "live"
                        ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/30"
                        : "bg-amber-500/10 text-amber-300 border-amber-500/30"}`}>
                        {p.mode || "test"}
                      </Badge>
                    </td>
                    <td className="px-5 py-3">
                      <div className="font-mono text-[11px] text-zinc-400 truncate max-w-[180px]">{p.razorpay_payment_id}</div>
                      <div className="font-mono text-[10px] text-zinc-600 truncate max-w-[180px]">{p.razorpay_order_id}</div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function Kpi({ icon: Icon, label, value, accent }) {
  return (
    <div className={`bg-zinc-950 border ${accent ? "border-emerald-500/30" : "border-white/10"} rounded-xl p-5`} data-testid={`payments-kpi-${label}`}>
      <div className="flex items-center gap-2 text-[10px] tracking-[0.2em] uppercase text-zinc-500">
        <Icon className={`w-3.5 h-3.5 ${accent ? "text-emerald-400" : ""}`}/> {label}
      </div>
      <div className={`font-display text-3xl tracking-tighter mt-2 ${accent ? "text-emerald-400" : ""}`}>{value}</div>
    </div>
  );
}
